"use client"

import { useEffect, useState, useSyncExternalStore } from "react"

import { useLocale, useTranslations } from "next-intl"

import { Button, Stack, Text } from "@/components/ui"
import { accent, ReferralDialog, ReferralDialogList } from "@/features/referrals/ReferralDialog"
import type { Locale } from "@/i18n/locales"
import { useMarketplaceReferralsEnabled, useReferralCodeOffer } from "@/lib/hooks/data/use-referrals"
import { formatMoneyWhole, moneyFromCents } from "@/lib/money"
import { dismissReferralInvite, readReferralAppliedRaw, readReferralInviteDismissed, readStoredReferralCode, subscribeReferralAttribution } from "@/lib/referrals/attribution"
import { errorStatus } from "@/platform/api/errors"

const AUTO_OPEN_DELAY_MS = 600

const readNothing = () => null

interface FriendInviteDialogProps {
  imageUrls: string[]
}

export const FriendInviteDialog = ({ imageUrls }: FriendInviteDialogProps) => {
  const t = useTranslations("referrals.invite")
  const locale = useLocale() as Locale
  const code = useSyncExternalStore(subscribeReferralAttribution, readStoredReferralCode, readNothing)
  const dismissed = useSyncExternalStore(subscribeReferralAttribution, readReferralInviteDismissed, readNothing)
  const applied = useSyncExternalStore(subscribeReferralAttribution, readReferralAppliedRaw, readNothing)
  const enabled = useMarketplaceReferralsEnabled()
  const pending = enabled && code !== null && applied === null && dismissed !== code
  const offer = useReferralCodeOffer(pending ? code : null)
  const visible = pending && !offer.isPending && !(offer.isError && errorStatus(offer.error) === 400)
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    if (!visible) return
    const timer = setTimeout(() => setSettled(true), AUTO_OPEN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [visible])

  if (code === null) return null

  const data = offer.data
  const discount = data ? formatMoneyWhole(moneyFromCents(data.friend_discount_cents, data.currency), locale) : null
  const name = data?.referrer_first_name
  const titleLead = discount === null ? t("titleLeadGeneric") : name ? t("titleLead", { name, discount }) : t("titleLeadAnonymous", { discount })
  const steps = [t.rich("step1", { accent }), t.rich("step2", { accent }), discount === null ? t.rich("step3Generic", { accent }) : t.rich("step3", { discount, accent })]
  const close = () => dismissReferralInvite(code)

  return (
    <ReferralDialog open={visible && settled} onClose={close} testId="friend-invite-dialog" imageUrls={imageUrls} titleLead={titleLead} titleEmphasis={t("titleEmphasis")}>
      <Stack gap="500" className="md:gap-600">
        <ReferralDialogList
          as="ol"
          label={t("steps")}
          items={steps.map((step, index) => ({
            key: String(index),
            lead: (
              <Text as="span" variant="caption" tone="brand" numeric className="border-fg-brand mt-50 shrink-0 rounded-full border px-250" aria-hidden>
                {String(index + 1).padStart(2, "0")}
              </Text>
            ),
            text: step,
          }))}
        />
        <Button fullWidth onClick={close} data-testid="friend-invite-cta">
          {t("cta")}
        </Button>
      </Stack>
    </ReferralDialog>
  )
}
